import { QueryDocumentSnapshot } from 'firebase/firestore'
import FirestoreUser, {
  shortDiagnosisTitles,
  shortRoleTitles,
} from '../types/FirestoreUser'
import { ReportInfo } from '../types/ReportTypes'

// Sort direction type
export type SortDirection = 'asc' | 'desc'

// Sort field type
export type SortField =
  | 'name'
  | 'reports'
  | 'diagnosis'
  | 'diagnosisDate'
  | 'deceased'
  | 'validated'
  | 'role'
  | 'state'
  | 'city'
  | 'country'
  | 'studies'
  | null

/**
 * Creates a comparator function for sorting users based on the specified field and direction
 *
 * @param field The field to sort by
 * @param direction Sort direction (asc or desc)
 * @param reports Reports data for comparison
 * @param lastNameFirst Whether to display last name first in the name field
 * @returns A comparator function for sorting
 */
export const createSortComparator = (
  field: SortField,
  direction: SortDirection,
  reports: Record<string, ReportInfo>,
  lastNameFirst: boolean
) => {
  return (
    a: QueryDocumentSnapshot<FirestoreUser>,
    b: QueryDocumentSnapshot<FirestoreUser>
  ) => {
    const aData = a.data()
    const bData = b.data()

    if (field === 'name') {
      return compareByName(aData, bData, direction, lastNameFirst)
    }

    if (field === 'reports') {
      return compareByReports(a.id, b.id, reports, direction)
    }

    if (field === 'studies') {
      return compareByStudies(aData, bData, direction)
    }

    if (field === 'diagnosis') {
      return compareByDiagnosis(aData, bData, direction)
    }

    if (field === 'diagnosisDate') {
      return compareByDiagnosisDate(aData, bData, direction)
    }

    if (field === 'deceased') {
      return compareByDeceased(aData, bData, direction)
    }

    if (field === 'validated') {
      return compareByValidated(aData, bData, direction)
    }

    if (field === 'role') {
      return compareByRole(aData, bData, direction)
    }

    if (field === 'country') {
      return compareByCountry(aData, bData, direction)
    }
    if (field === 'state') {
      return compareByState(aData, bData, direction)
    }
    if (field === 'city') {
      return compareByCity(aData, bData, direction)
    }

    return 0
  }
}

/**
 * Compare users by name
 */
const compareByName = (
  aData: FirestoreUser,
  bData: FirestoreUser,
  direction: SortDirection,
  lastNameFirst: boolean
) => {
  const aName = lastNameFirst
    ? `${aData.profile?.lastName || ''}, ${aData.profile?.firstName || ''}`
    : `${aData.profile?.firstName || ''} ${aData.profile?.lastName || ''}`
  const bName = lastNameFirst
    ? `${bData.profile?.lastName || ''}, ${bData.profile?.firstName || ''}`
    : `${bData.profile?.firstName || ''} ${bData.profile?.lastName || ''}`

  return direction === 'asc'
    ? aName.localeCompare(bName)
    : bName.localeCompare(aName)
}

/**
 * Compare users by reports
 */
const compareByReports = (
  aId: string,
  bId: string,
  reports: Record<string, ReportInfo>,
  direction: SortDirection
) => {
  // Get the most recent report date for each user or an empty string if none
  const aReportDate = reports[aId]?.mostRecentReport || ''
  const bReportDate = reports[bId]?.mostRecentReport || ''

  // Sort users with no reports to the end regardless of sort direction
  if (!aReportDate && !bReportDate) return 0
  if (!aReportDate) return 1
  if (!bReportDate) return -1

  // Apply sort direction
  return direction === 'desc'
    ? bReportDate.localeCompare(aReportDate) // Most recent first
    : aReportDate.localeCompare(bReportDate) // Oldest first
}

/**
 * Compare users by studies
 */
const compareByStudies = (
  aData: FirestoreUser,
  bData: FirestoreUser,
  direction: SortDirection
) => {
  const aStudies = aData.studies || []
  const bStudies = bData.studies || []

  // If one user has studies and the other doesn't, the one with studies comes first
  if (aStudies.length === 0 && bStudies.length === 0) return 0
  if (aStudies.length === 0) return direction === 'asc' ? 1 : -1
  if (bStudies.length === 0) return direction === 'asc' ? -1 : 1

  // Extract the study names, handling both string and StudyData formats
  const aFirstStudyName =
    typeof aStudies[0] === 'string' ? aStudies[0] : aStudies[0].name

  const bFirstStudyName =
    typeof bStudies[0] === 'string' ? bStudies[0] : bStudies[0].name

  return direction === 'asc'
    ? aFirstStudyName.localeCompare(bFirstStudyName)
    : bFirstStudyName.localeCompare(aFirstStudyName)
}

/**
 * Compare users by diagnosis
 */
const compareByDiagnosis = (
  aData: FirestoreUser,
  bData: FirestoreUser,
  direction: SortDirection
) => {
  const aDiagnosis =
    shortDiagnosisTitles[aData.profile?.diagnosis || 'administrator'] || ''
  const bDiagnosis =
    shortDiagnosisTitles[bData.profile?.diagnosis || 'administrator'] || ''

  return direction === 'asc'
    ? aDiagnosis.localeCompare(bDiagnosis)
    : bDiagnosis.localeCompare(aDiagnosis)
}

/**
 * Compare users by diagnosis verification date (profile.diagnosisVerificationDate)
 * Dates are stored as strings in YYYY-MM-DD format. Users with no date are
 * sorted to the end regardless of direction.
 */
const compareByDiagnosisDate = (
  aData: FirestoreUser,
  bData: FirestoreUser,
  direction: SortDirection
) => {
  const aDate = aData.profile?.diagnosisVerificationDate || ''
  const bDate = bData.profile?.diagnosisVerificationDate || ''

  if (!aDate && !bDate) return 0
  if (!aDate) return 1
  if (!bDate) return -1

  return direction === 'asc'
    ? aDate.localeCompare(bDate)
    : bDate.localeCompare(aDate)
}

/**
 * Compare users by role
 */
const compareByRole = (
  aData: FirestoreUser,
  bData: FirestoreUser,
  direction: SortDirection
) => {
  const aRole = aData.roles?.map((r) => shortRoleTitles[r]).join('/') || ''
  const bRole = bData.roles?.map((r) => shortRoleTitles[r]).join('/') || ''

  return direction === 'asc'
    ? aRole.localeCompare(bRole)
    : bRole.localeCompare(aRole)
}

/**
 * Compare users by country
 */
const compareByCountry = (
  aData: FirestoreUser,
  bData: FirestoreUser,
  direction: SortDirection
) => {
  const aCountry = aData.profile?.country || ''
  const bCountry = bData.profile?.country || ''

  return direction === 'asc'
    ? aCountry.localeCompare(bCountry)
    : bCountry.localeCompare(aCountry)
}

/**
 * Compare users by deceased status
 */
const compareByDeceased = (
  aData: FirestoreUser,
  bData: FirestoreUser,
  direction: SortDirection
) => {
  const aIsDeceased = aData.profile?.isDeceased ? 1 : 0
  const bIsDeceased = bData.profile?.isDeceased ? 1 : 0

  // When sorting by deceased status, we want deceased users (Yes) to appear first
  // when sorting in descending order and last when sorting in ascending order
  return direction === 'asc'
    ? aIsDeceased - bIsDeceased
    : bIsDeceased - aIsDeceased
}

/**
 * Compare users by validation status
 */
const compareByValidated = (
  aData: FirestoreUser,
  bData: FirestoreUser,
  direction: SortDirection
) => {
  // If either user doesn't need validation (admin or medical), place them differently in results
  const aRequiresValidation =
    aData.profile?.diagnosis &&
    !['administrator', 'medical'].includes(aData.profile.diagnosis)
  const bRequiresValidation =
    bData.profile?.diagnosis &&
    !['administrator', 'medical'].includes(bData.profile.diagnosis)

  // Handle cases where validation isn't applicable
  if (!aRequiresValidation && !bRequiresValidation) return 0
  if (!aRequiresValidation) return direction === 'asc' ? 1 : -1
  if (!bRequiresValidation) return direction === 'asc' ? -1 : 1

  // Both users require validation, compare their validation status
  const aIsValidated = aData.profile?.diagnosisVerified ? 1 : 0
  const bIsValidated = bData.profile?.diagnosisVerified ? 1 : 0

  // When sorting by validation status, validated users (Yes) should appear first
  // when sorting in descending order and last when sorting in ascending order
  return direction === 'asc'
    ? aIsValidated - bIsValidated
    : bIsValidated - aIsValidated
}

/**
 * Compare users by state/province
 */
const compareByState = (
  aData: FirestoreUser,
  bData: FirestoreUser,
  direction: SortDirection
) => {
  const aState = aData.profile?.state || ''
  const bState = bData.profile?.state || ''

  return direction === 'asc' ? aState.localeCompare(bState) : bState.localeCompare(aState)
}

/**
 * Compare users by city
 */
const compareByCity = (
  aData: FirestoreUser,
  bData: FirestoreUser,
  direction: SortDirection
) => {
  const aCity = aData.profile?.city || ''
  const bCity = bData.profile?.city || ''

  return direction === 'asc' ? aCity.localeCompare(bCity) : bCity.localeCompare(aCity)
}
